import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import axios from 'axios';

export const requestOTP = createAsyncThunk('portalAuth/requestOTP', async (email, { rejectWithValue }) => {
    try {
        const res = await axios.post(`${baseURL}/portal/auth/request-otp`, { email });
        return res.data;
    } catch (err) { return rejectWithValue(err.response?.data?.message || 'Failed to send OTP'); }
});

export const verifyOTP = createAsyncThunk('portalAuth/verifyOTP', async ({ email, otp }, { rejectWithValue }) => {
    try {
        const res = await axios.post(`${baseURL}/portal/auth/verify-otp`, { email, otp });
        if (res.data.data.multipleBusinesses) {
            localStorage.setItem('tempBusinesses', JSON.stringify(res.data.data.businesses));
            return { multipleBusinesses: true, businesses: res.data.data.businesses };
        }
        const { token, customer } = res.data.data;
        localStorage.setItem('portalToken', token);
        localStorage.setItem('portalCustomer', JSON.stringify(customer));
        return { token, customer };
    } catch (err) { return rejectWithValue(err.response?.data?.message || 'Verification failed'); }
});

export const activatePortal = createAsyncThunk('portalAuth/activate', async (token, { rejectWithValue }) => {
    try {
        const res = await axios.get(`${baseURL}/portal/activate/${token}`);
        return res.data.data;
    } catch (err) { return rejectWithValue(err.response?.data?.message || 'Activation failed'); }
});

export const logoutPortal = createAsyncThunk('portalAuth/logout', async () => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalCustomer');
    localStorage.removeItem('tempBusinesses');
    return null;
});

export const fetchPortalBusiness = createAsyncThunk('portalAuth/fetchBusiness', async (_, { rejectWithValue }) => {
    try {
        const res = await PortalAxios.PortalRequest({
            method: 'get',
            url: `${baseURL}/portal/business`
        });
        return res.data.data;
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Failed to fetch business');
    }
});

export const fetchMyPortalBusinesses = createAsyncThunk('portalAuth/fetchMyBusinesses', async (_, { rejectWithValue }) => {
    try {
        const res = await PortalAxios.PortalRequest({
            method: 'get',
            url: `${baseURL}/portal/auth/my-businesses`
        });
        return res.data.data.businesses;
    } catch (err) {
        return rejectWithValue(err.response?.data?.message || 'Failed to fetch businesses');
    }
});

const portalAuthSlice = createSlice({
    name: 'portalAuth',
    initialState: {
        customer: (() => { try { return JSON.parse(localStorage.getItem('portalCustomer')); } catch { return null; } })(),
        portalToken: localStorage.getItem('portalToken'),
        tempBusinesses: (() => { try { return JSON.parse(localStorage.getItem('tempBusinesses')); } catch { return null; } })(),
        myBusinesses: [],
        businessData: null,
        loading: false,
        otpSent: false,
        activationData: null,
        error: null,
    },
    reducers: {
        clearPortalError: (state) => { state.error = null; },
        updateCustomer: (state, action) => {
            state.customer = { ...state.customer, ...action.payload };
            localStorage.setItem('portalCustomer', JSON.stringify(state.customer));
        },
        selectPortalBusiness: (state, action) => {
            const { token, customer } = action.payload;
            state.portalToken = token;
            state.customer = customer;
            state.tempBusinesses = null;
            localStorage.setItem('portalToken', token);
            localStorage.setItem('portalCustomer', JSON.stringify(customer));
            localStorage.removeItem('tempBusinesses');
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPortalBusiness.fulfilled, (state, action) => {
                state.businessData = action.payload;
                if (action.payload.name) {
                    state.customer = { ...state.customer, businessName: action.payload.name };
                    localStorage.setItem('portalCustomer', JSON.stringify(state.customer));
                }
            })
            .addCase(requestOTP.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(requestOTP.fulfilled, (state) => { state.loading = false; state.otpSent = true; })
            .addCase(requestOTP.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            .addCase(verifyOTP.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(verifyOTP.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.multipleBusinesses) {
                    state.tempBusinesses = action.payload.businesses;
                } else {
                    state.customer = action.payload.customer;
                    state.portalToken = action.payload.token;
                    state.tempBusinesses = null;
                }
            })
            .addCase(verifyOTP.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            .addCase(activatePortal.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(activatePortal.fulfilled, (state, action) => { state.loading = false; state.activationData = action.payload; })
            .addCase(activatePortal.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            .addCase(logoutPortal.fulfilled, (state) => {
                state.customer = null;
                state.portalToken = null;
                state.otpSent = false;
                state.tempBusinesses = null;
                state.myBusinesses = [];
            })
            
            .addCase(fetchMyPortalBusinesses.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchMyPortalBusinesses.fulfilled, (state, action) => {
                state.loading = false;
                state.myBusinesses = action.payload;
            })
            .addCase(fetchMyPortalBusinesses.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    }
});

export const { clearPortalError, selectPortalBusiness } = portalAuthSlice.actions;
export default portalAuthSlice.reducer;
